import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  Link,
  Box,
  useTheme
} from '@mui/material';
import {
  Home,
  NavigateNext,
  Person,
  EventNote,
  MonitorHeart,
  LocalHospital,
  History,
  Assessment
} from '@mui/icons-material';

const Breadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const getPathSegments = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    return segments;
  };

  const getSegmentInfo = (segment, index, segments) => {
    const pathMap = {
      '': { label: 'Tableau de Bord', icon: <Home />, path: '/' },
      'patients': { label: 'Patients', icon: <Person />, path: '/patients' },
      'consultations': { label: 'Consultations', icon: <EventNote />, path: '/consultations' },
      'vitals': { label: 'Constantes Vitales', icon: <MonitorHeart />, path: '/vitals' },
      'treatments': { label: 'Traitements', icon: <LocalHospital />, path: '/treatments' },
      'history': { label: 'Antécédents', icon: <History />, path: '/history' },
      'reports': { label: 'Rapports', icon: <Assessment />, path: '/reports' },
      'new': { label: 'Nouveau', icon: null, path: null },
    };

    // Si c'est un ID numérique, c'est probablement un détail
    if (/^\d+$/.test(segment)) {
      const parentSegment = segments[index - 1];
      const parentInfo = pathMap[parentSegment];
      
      if (parentInfo) {
        return {
          label: `Détails`,
          icon: parentInfo.icon,
          path: `/${parentSegment}/${segment}`,
          isDetail: true
        };
      }
    }

    return pathMap[segment] || { 
      label: segment.charAt(0).toUpperCase() + segment.slice(1), 
      icon: null, 
      path: null 
    };
  };

  const segments = getPathSegments();
  const breadcrumbItems = [];

  // Toujours commencer par l'accueil
  breadcrumbItems.push({
    label: 'Tableau de Bord',
    icon: <Home />,
    path: '/',
    isActive: location.pathname === '/'
  });

  // Construire le chemin progressivement
  let currentPath = '';
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const segmentInfo = getSegmentInfo(segment, index, segments);
    
    breadcrumbItems.push({
      ...segmentInfo,
      path: segmentInfo.path || currentPath,
      isActive: location.pathname === currentPath,
      isLast: index === segments.length - 1
    });
  });

  const handleClick = (path) => {
    if (path && path !== location.pathname) {
      navigate(path);
    }
  };

  if (breadcrumbItems.length <= 1) {
    return null; // Ne pas afficher les breadcrumbs sur la page d'accueil
  }

  return (
    <Box sx={{ mb: 3 }}>
      <MuiBreadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-separator': {
            color: 'text.secondary',
          },
        }}
      >
        {breadcrumbItems.map((item, index) => (
          <Box
            key={index}
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              cursor: item.path && !item.isLast ? 'pointer' : 'default',
              '&:hover': item.path && !item.isLast ? {
                backgroundColor: 'action.hover',
                borderRadius: 1,
                px: 1,
                py: 0.5,
              } : {},
            }}
            onClick={() => handleClick(item.path)}
          >
            {item.icon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: item.isActive ? 'primary.main' : 'text.secondary',
                  fontSize: '1rem',
                }}
              >
                {item.icon}
              </Box>
            )}
            
            {item.isLast ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }}
              >
                {item.label}
              </Typography>
            ) : (
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  handleClick(item.path);
                }}
                sx={{
                  color: item.isActive ? 'primary.main' : 'text.secondary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: item.isActive ? 500 : 400,
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.main',
                  },
                }}
              >
                {item.label}
              </Link>
            )}
          </Box>
        ))}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;
